/**
  * NOTE: Observers can block Observables from being garbage collected if Observable/Observer was not properly disposed.
  * @see dispose() method on Observable / Observer to release the reference.
  * @TODO This can be fixed using WeakRef which is a ES2021 feature.
  */
export class Observer {
    constructor(callback, remove_on_call, _observable) {
        this.callback = callback;
        this.remove_on_call = remove_on_call;
        this._observable = _observable;
        this.enabled = true;
    }
    /**
     * Removes the observer from its observable
     */
    dispose() {
        try {
            this._observable.remove(this);
        }
        catch (_) { }
    }
}
export class Observable {
    get has_active_observers() {
        let has_enabled_observer = false;
        for (const observer of this._observers) {
            if (observer.enabled) {
                has_enabled_observer = true;
                break;
            }
        }
        return has_enabled_observer;
    }
    get has_observers() {
        return this._observers.size > 0;
    }
    constructor(observers_changed_callback = null) {
        this.observers_changed_callback = observers_changed_callback;
        // Since this should hold both full instances and interfaces we can not make it type safe.
        // This set should be manipulated only via TypeSafe methods.
        this._observers = new Set();
        this._revokable = Proxy.revocable(this, {});
    }
    /**
     * Notify observers of a new value of type T. This should only be called from the owner of the observable
     * @param val the new value to notify observers about
     */
    notify(sender, val) {
        // Call all observers in order of insertion
        for (const observer of this._observers) {
            if (!observer.enabled)
                continue;
            observer.callback(sender, val);
            if (observer.remove_on_call) {
                // This will call observers_changed_callback for every disposed Observer
                observer.dispose();
            }
        }
    }
    /**
     * Adds a new callback to be called when Observable notifies
     * @param callback Function to be called with the new value when observable notifies
     * @returns Observer<T> object that can be used to later remove the Observer
     */
    add(callback) {
        const observer = new Observer(callback, false, this._revokable.proxy);
        this._observers.add(observer);
        if (this.observers_changed_callback !== null)
            this.observers_changed_callback(this._observers.size);
        return observer;
    }
    /**
     * Adds a new callback to be called when Observable notifies, will remove itself after the first call
     * @param callback Function to be called with the new value when observable notifies
     * @returns Observer<T> object that can be used to later remove the Observer
     */
    add_once(callback) {
        const observer = new Observer(callback, true, this._revokable.proxy);
        this._observers.add(observer);
        return observer;
    }
    /**
     * Removes the observer from this observable
     * @param observer observer to remove
     * @returns True if the observer was removed, false otherwise
     */
    remove(observer) {
        if (this._observers.has(observer)) {
            const was_deleted = this._observers.delete(observer);
            if (was_deleted && this.observers_changed_callback !== null)
                this.observers_changed_callback(this._observers.size);
            return true;
        }
        return false;
    }
    clear() {
        this._observers.forEach(observer => observer.dispose());
        this._observers.clear();
        if (this.observers_changed_callback !== null)
            this.observers_changed_callback(this._observers.size);
    }
    dispose() {
        this._observers.clear();
        this._revokable.revoke();
        this.observers_changed_callback = null;
    }
}
//# sourceMappingURL=Observable.js.map